<template>
  <el-dialog
    :visible.sync="show"
    :with-header="false"
    :show-close="true"
    :append-to-body="true"
    :close-on-click-modal="false"
    width="560px"
    top="8vh"
    custom-class="pf-dialog"
    @opened="onOpened"
    @closed="onClosed"
  >
    <div slot="title" class="pf__head">
      <div class="pf__title">{{ $t("products.finderTitle") }}</div>
      <div class="pf__hint">{{ $t("products.finderHint") }}</div>
    </div>

    <div class="pf__search">
      <el-input
        ref="searchInput"
        v-model="keyword"
        clearable
        prefix-icon="el-icon-search"
        :placeholder="$t('products.finderSearch')"
        @input="onKeyword"
      ></el-input>
    </div>

    <!-- 结果计数 -->
    <div v-if="!loading" class="pf__count">{{ $t("products.resultsCount", { n: total }) }}</div>

    <div v-loading="loading" class="pf__results">
      <template v-if="list.length > 0">
        <button v-for="row in list" :key="row.id" type="button" class="pf__row" @click="use(row)">
          <el-image class="pf__img" :src="firstImg(row.url)" fit="contain">
            <div slot="error" class="pf__ph"><i class="el-icon-goods"></i></div>
            <div slot="placeholder" class="pf__ph"><i class="el-icon-goods"></i></div>
          </el-image>
          <div class="pf__meta">
            <span class="pf__name">{{ row.name }}</span>
            <span v-if="row.content" class="pf__desc">{{ row.content }}</span>
          </div>
          <span class="pf__use">{{ $t("products.useThis") }}</span>
        </button>
      </template>
      <div v-else-if="!loading" class="pf__empty">
        <i class="el-icon-search pf__empty-icon"></i>
        <div class="pf__empty-text">{{ keyword ? $t("common.noData") : $t("products.emptyLibrary") }}</div>
      </div>
    </div>

    <div v-if="total > pageSize" class="pf__pager">
      <el-pagination
        background
        small
        layout="prev, pager, next"
        :current-page="page.pageIndex"
        :total="total"
        :page-size="pageSize"
        @current-change="changePage"
      ></el-pagination>
    </div>

    <div slot="footer" class="pf__foot">
      <span class="pf__foot-label">{{ $t("products.finderHint") }}</span>
      <el-button type="primary" plain icon="el-icon-plus" @click="createNew">{{ $t("products.createNew") }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
  // 搜索优先的新增漏斗（设计指南 §8.3）：先搜共享素材库，命中即「使用」克隆，
  // 未命中再「新建」。后端无 obtain，使用=前端字段拷贝 → 由父级走 goods/insert。
  export default {
    name: "ProductFinderDialog",
    props: {
      visible: { type: Boolean, default: false }
    },
    data() {
      return {
        keyword: "",
        loading: false,
        list: [],
        total: 0,
        pageSize: 8,
        page: { pageIndex: 1 },
        debounceTimer: null
      };
    },
    computed: {
      show: {
        get() {
          return this.visible;
        },
        set(v) {
          this.$emit("update:visible", v);
        }
      }
    },
    methods: {
      firstImg(url) {
        if (!url) {
          return "";
        }
        return String(url).split(",")[0].trim();
      },
      onOpened() {
        this.keyword = "";
        this.page.pageIndex = 1;
        this.fetch();
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      },
      onClosed() {
        this.list = [];
        this.total = 0;
      },
      onKeyword() {
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
          this.page.pageIndex = 1;
          this.fetch();
        }, 300);
      },
      changePage(p) {
        this.page.pageIndex = p;
        this.fetch();
      },
      fetch() {
        this.loading = true;
        this.$api
          .request(this.$url.ProductLibrary.page, this.$method.get, {
            name: this.keyword || "",
            pageIndex: this.page.pageIndex,
            pageSize: this.pageSize
          })
          .then(res => {
            if (res.code === 0 && res.data) {
              // 按名称字母序展示，忽略大小写（后端无排序参数，按当前页排序）
              this.list = (res.data.records || [])
                .slice()
                .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base", numeric: true }));
              this.total = parseInt(res.data.total) || 0;
            }
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
          });
      },
      // 使用素材：字段拷贝（去除 id），交由父级打开新建表单并保存为自有商品
      use(row) {
        const copy = {};
        ["name", "url", "content", "introduction", "notes"].forEach(k => {
          copy[k] = row[k] !== undefined && row[k] !== null ? row[k] : "";
        });
        this.$emit("use", copy);
        this.show = false;
      },
      createNew() {
        this.$emit("create");
        this.show = false;
      }
    }
  };
</script>

<style scoped lang="scss">
  .pf__head {
    padding-right: 24px;
  }
  .pf__title {
    font-family: var(--vtm-display);
    font-size: 18px;
    font-weight: 600;
    color: var(--vtm-heading);
  }
  .pf__hint {
    font-size: 12.5px;
    color: var(--vtm-ink-3);
    margin-top: 3px;
  }

  .pf__search {
    margin-bottom: 6px;
  }

  .pf__count {
    font-size: 12px;
    color: var(--vtm-ink-3);
    padding: 2px 2px 8px;
  }

  .pf__results {
    min-height: 160px;
    max-height: 420px;
    overflow-y: auto;
  }

  .pf__row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
    padding: 10px 8px;
    border: none;
    border-bottom: 1px solid var(--vtm-line);
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background-color var(--vtm-speed) ease;

    &:hover {
      background: var(--vtm-mint-soft);
    }
  }

  .pf__img {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border-radius: var(--vtm-r-sm);
    background: var(--vtm-well);
    overflow: hidden;
  }
  .pf__ph {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: var(--vtm-ink-3);
    font-size: 18px;
    background: var(--vtm-well);
  }

  .pf__meta {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .pf__name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--vtm-ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .pf__desc {
    font-size: 12px;
    color: var(--vtm-ink-3);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 2px;
  }
  .pf__use {
    flex-shrink: 0;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--vtm-mint-ink);
  }

  .pf__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 44px 20px;
    text-align: center;
  }
  .pf__empty-icon {
    font-size: 30px;
    color: var(--vtm-ink-3);
  }
  .pf__empty-text {
    font-size: 13px;
    color: var(--vtm-ink-2);
  }

  .pf__pager {
    text-align: center;
    padding-top: 10px;
  }

  .pf__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .pf__foot-label {
    font-size: 12px;
    color: var(--vtm-ink-3);
    text-align: left;
  }
</style>
